import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'danger-subtle'
  | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and blocks further clicks. Width is held steady to avoid layout shift. */
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  fullWidth?: boolean;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'btn-glow bg-brand text-white hover:bg-brand-hover active:bg-brand-active border border-transparent',
  secondary:
    'bg-surface text-text-primary border border-border-default shadow-xs hover:bg-surface-hover active:bg-surface-active',
  ghost: 'bg-transparent text-text-secondary border border-transparent hover:bg-surface-hover hover:text-text-primary',
  danger:
    'bg-danger text-white shadow-xs hover:bg-danger-hover border border-transparent',
  'danger-subtle':
    'bg-danger-subtle text-danger-text border border-danger-border hover:bg-danger hover:text-white',
  link: 'bg-transparent text-brand-text underline-offset-4 hover:underline border-none shadow-none px-0',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[0.8125rem] gap-1.5 rounded-md',
  md: 'h-9 px-3.5 text-sm gap-2 rounded-md',
  lg: 'h-11 px-5 text-[0.9375rem] gap-2 rounded-lg',
  icon: 'h-9 w-9 justify-center rounded-md',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    leadingIcon,
    trailingIcon,
    fullWidth,
    className,
    children,
    disabled,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      // A loading button must not be clickable twice: the second click is the duplicate mutation.
      disabled={disabled || loading}
      // Communicates the busy state to assistive technology, which cannot see the spinner.
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex select-none items-center font-medium whitespace-nowrap',
        'transition-[background-color,border-color,color,box-shadow,transform] duration-150',
        'active:translate-y-px',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full justify-center',
        className,
      )}
      {...props}
    >
      {loading ? (
        <Loader2 aria-hidden className="size-4 shrink-0 animate-spin" />
      ) : (
        leadingIcon
      )}
      {/*
        Rendered for every size, including `icon`. Icon buttons pass their glyph as children, and
        this line used to drop children for that size - which drew six controls across the app
        (sign out, theme, every dialog's close) as empty transparent squares.
      */}
      {children}
      {!loading && trailingIcon}
    </button>
  );
});
